import CustomModal from "@/shared/components/ui/Modal/CustomModal";
import { zodResolver } from "@hookform/resolvers/zod";
import React from "react";
import { useForm } from "react-hook-form";
import { X } from "lucide-react";
import {
  addContactFormSchema,
  isLinkedInCompanyUrl,
} from "../formSchema/addContactFormSchema";
import AddContactForm from "./AddContactForm";
import { useAddContactManually } from "../hooks/useContacts";
import { showErrorFromMutation, showWarning } from "@/shared/utils/toast";
import useUnsavedChangesWarning from "@/shared/hooks/useUnsavedChangesWarning";

function mapCreateContactServerError(message) {
  if (!message || typeof message !== "string") {
    return { field: null, message: "Failed to add contact. Please try again." };
  }
  const lower = message.toLowerCase();
  if (lower.includes("whatsapp")) {
    return { field: "whatsappNumber", message };
  }
  if (lower.includes("email")) {
    return { field: "email", message };
  }
  if (lower.includes("linkedin")) {
    return { field: "linkedinUrl", message };
  }
  return { field: null, message };
}

const AddContactModal = ({ isOpen, onClose }) => {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isDirty },
    setValue,
    watch,
    setError,
    clearErrors,
  } = useForm({
    mode: "onChange",
    resolver: zodResolver(addContactFormSchema),
    defaultValues: {
      // headline: "",
      // tags: [],
      companySize: "",
      industry: [],
      country: [],
      companyLinkedinUrl: "",
      companyWebsite: "",
      leadSource: "",
      socialLeadId: "",
      buyingRole: "",
      persona: "",
      description: "",
      emailOptOut: false,
    },
  });

  const { mutate: addContactManually, isPending: isAddingContactManually } =
    useAddContactManually();

  const showCompanyLinkedInWarning = () => {
    showWarning(
      "Company LinkedIn URL not allowed",
      "Please enter the contact's personal LinkedIn profile URL (linkedin.com/in/username).",
    );
  };

  const onInvalid = () => {
    if (isLinkedInCompanyUrl(watch("linkedinUrl"))) {
      showCompanyLinkedInWarning();
    }
  };

  const onSubmit = (data) => {
    clearErrors(["email", "whatsappNumber", "linkedinUrl"]);
    const industryJoined = Array.isArray(data.industry)
      ? data.industry.map(String).filter(Boolean).join(", ")
      : String(data.industry ?? "");
    const countryJoined = Array.isArray(data.country)
      ? data.country.map(String).filter(Boolean).join(", ")
      : String(data.country ?? "");
    addContactManually(
      {
        ...data,
        industry: industryJoined,
        country: countryJoined,
      },
      {
        onSuccess: () => {
          handleClose();
        },
        onError: (error) => {
          const raw = error?.message || "";
          const { field, message } = mapCreateContactServerError(raw);
          if (field) {
            setError(field, { type: "server", message });
            if (field === "linkedinUrl" && raw.toLowerCase().includes("company")) {
              showCompanyLinkedInWarning();
            }
          } else {
            showErrorFromMutation(error, message);
          }
        },
      },
    );
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  useUnsavedChangesWarning(Boolean(isOpen && isDirty));

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={handleClose}
      width="500px"
      className="p-6"
    >
      <div className="w-full flex flex-col gap-4">
        <div className="flex justify-between items-start w-full">
          <h2 className="text-[0.9825rem] font-semibold text-gray-800 dark:text-gray-100">
            Add Contact
          </h2>
          <button
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
            onClick={handleClose}
          >
            <X size={20} className="text-gray-800 dark:text-gray-200" />
          </button>
        </div>
        <AddContactForm
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          onInvalid={onInvalid}
          register={register}
          control={control}
          errors={errors}
          isSubmitting={isAddingContactManually}
          onCancel={handleClose}
          setValue={setValue}
          watch={watch}
        />
      </div>
    </CustomModal>
  );
};

export default AddContactModal;
